// Details.js - Universal File Manager for BOTH Manufacturing & Lot Tracking
// ✅ mode="manufacturing" → api/manufacturing/{id}/images/...
// ✅ mode="lot_tracking"  → suppliers/{supplierId}/lot-tracking/{id}/images/...
import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { FaRegEye } from "react-icons/fa";
import { IoAddCircleSharp, IoCloseCircleSharp } from "react-icons/io5";
import { MdOutlineDownloadForOffline } from "react-icons/md";
import { BsArrowRepeat } from "react-icons/bs";
import { RiDeleteBin6Fill } from "react-icons/ri";
import { FiSave } from "react-icons/fi";
import Swal from 'sweetalert2';

const API_BASE_URL = 'https://super-backend.hira-tech.co.uk';

// ============================================================
// AUTH HELPERS
// ============================================================
const getAuthToken = () => {
  const possibleKeys = ['access_token', 'token', 'accessToken', 'authToken', 'jwt'];
  for (let key of possibleKeys) {
    const val = localStorage.getItem(key);
    if (val && val !== 'null' && val !== 'undefined') {
      try {
        const parsed = JSON.parse(val);
        if (parsed && typeof parsed === 'object') {
          const inner = parsed.access || parsed.token || parsed.access_token;
          if (inner) return inner;
        }
      } catch (e) {
        return val;
      }
      return val;
    }
  }
  return null;
};

const buildHeaders = (withJson = true) => {
  const h = {};
  if (withJson) h['Content-Type'] = 'application/json';
  const token = getAuthToken();
  if (token && token !== 'null' && token !== 'undefined') {
    h['Authorization'] = `Bearer ${token}`;
  }
  return h;
};

// ============================================================
// HELPERS
// ============================================================
const mapBackendTypeToCategory = (backendType) => {
  switch (backendType) {
    case 'image': return 'image';
    case 'cad':
    case 'scan': return 'scan';
    case 'zip': return 'zip';
    default: return 'other';
  }
};

const detectCategoryFromName = (name = '') => {
  const lower = name.toLowerCase();
  if (/\.(jpg|jpeg|png|gif|bmp|webp|jfif)$/.test(lower)) return 'image';
  if (/\.(stl|ply|obj|dcm|slddrw)$/.test(lower) || lower.includes('scan')) return 'scan';
  if (/\.(zip|rar|7z)$/.test(lower)) return 'zip';
  return 'other';
};

const extractFileUrl = (f) => {
  if (!f) return null;
  if (f.file_url) return f.file_url;
  if (f.image_url) return f.image_url;
  if (f.url) return f.url;
  if (typeof f.image === 'string' && f.image) {
    if (f.image.startsWith('http')) return f.image;
    if (f.image.startsWith('/')) return `${API_BASE_URL}${f.image}`;
    return `${API_BASE_URL}/${f.image}`;
  }
  return null;
};

const extractFilename = (f, fileUrl) => {
  if (!f) return 'file';
  if (f.original_filename) return f.original_filename;
  if (f.file_name) return f.file_name;
  if (f.name) return f.name;
  if (fileUrl) {
    const parts = fileUrl.split('/');
    return parts[parts.length - 1] || 'file';
  }
  return 'file';
};

// ============================================================
// UNIVERSAL DETAILS COMPONENT
// ============================================================
const Details = forwardRef(({
  trackingId,
  supplierId = null,      // ✅ NEW: for lot_tracking mode
  mode = 'manufacturing', // ✅ 'manufacturing' | 'lot_tracking'
  isNew = false,
  isEdit = false,
}, ref) => {
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  // ============================================================
  // URL BUILDERS (mode-based)
  // ============================================================
  const getListUrl = () => {
    if (mode === 'lot_tracking' && supplierId) {
      return `${API_BASE_URL}/inventory/suppliers/${supplierId}/lot-tracking/${trackingId}/images/all/`;
    }
    return `${API_BASE_URL}/inventory/api/manufacturing/${trackingId}/images/all/`;
  };

  const getUploadUrl = () => {
    if (mode === 'lot_tracking' && supplierId) {
      return `${API_BASE_URL}/inventory/suppliers/${supplierId}/lot-tracking/${trackingId}/images/`;
    }
    return `${API_BASE_URL}/inventory/api/manufacturing/${trackingId}/images/`;
  };

  const getDeleteUrl = (imageId) => {
    if (mode === 'lot_tracking' && supplierId) {
      return `${API_BASE_URL}/inventory/suppliers/${supplierId}/lot-tracking/${trackingId}/images/${imageId}/delete/`;
    }
    return `${API_BASE_URL}/inventory/api/manufacturing/${trackingId}/images/${imageId}/`;
  };

  // ============================================================
  // EXPOSE saveFiles() TO PARENT
  // ============================================================
  useImperativeHandle(ref, () => ({
    saveFiles: async (newTrackingId) => await handleSaveFiles(newTrackingId),
    hasUnsavedFiles: () => files.some(f => !f.isSaved && f.file),
    getUnsavedCount: () => files.filter(f => !f.isSaved && f.file).length,
  }));

  // ============================================================
  // FETCH EXISTING FILES
  // ============================================================
  useEffect(() => {
    const fetchFiles = async () => {
      if (!trackingId || trackingId === 'new' || isNew) return;
      if (mode === 'lot_tracking' && !supplierId) {
        console.warn('⚠️ lot_tracking mode requires supplierId');
        return;
      }

      try {
        setLoading(true);
        const url = getListUrl();
        console.log(`📥 [${mode}] Fetching files from:`, url);

        const response = await fetch(url, { headers: buildHeaders(false) });

        if (!response.ok) {
          console.warn('⚠️ Fetch files failed:', response.status);
          setFiles([]);
          return;
        }

        const data = await response.json();
        const rawList = Array.isArray(data) ? data : (data.files || data.images || []);
        console.log(`📦 [${mode}] Raw files (${rawList.length}):`, rawList);

        const fetchedFiles = rawList.map(f => {
          const fileUrl = extractFileUrl(f);
          const filename = extractFilename(f, fileUrl);
          const category = mapBackendTypeToCategory(f.image_type || f.category || f.file_type);

          return {
            id: f.id,
            name: filename,
            type: f.file_type || '',
            category,
            size: f.file_size || 0,
            date: f.uploaded_at ? new Date(f.uploaded_at).toLocaleDateString() : new Date().toLocaleDateString(),
            updated: f.uploaded_at ? new Date(f.uploaded_at).toLocaleDateString() : new Date().toLocaleDateString(),
            from: f.uploaded_by || 'Admin',
            url: fileUrl,
            preview: category === 'image' ? fileUrl : null,
            isSaved: true,
            file: null,
            isMirror: !!(f.source_lot_tracking_image_id),
          };
        });

        console.log(`✅ [${mode}] Loaded ${fetchedFiles.length} files`);
        setFiles(fetchedFiles);
      } catch (err) {
        console.error('❌ Error fetching files:', err);
        setFiles([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackingId, isNew, mode, supplierId]);

  // ============================================================
  // ADD FILE (local queue)
  // ============================================================
  const handleFileUpload = (event) => {
    const uploadedFiles = Array.from(event.target.files);
    const newFiles = uploadedFiles.map(file => {
      const category = detectCategoryFromName(file.name);
      return {
        id: `local_${Date.now()}_${Math.random()}`,
        name: file.name,
        type: file.type,
        category,
        size: file.size,
        date: new Date().toLocaleDateString(),
        updated: new Date().toLocaleDateString(),
        from: 'Admin',
        file: file,
        preview: file.type.includes('image') ? URL.createObjectURL(file) : null,
        isSaved: false,
      };
    });
    setFiles(prev => [...prev, ...newFiles]);

    Swal.fire({
      icon: 'info',
      title: 'File Added',
      text: 'Click "Save Files" to upload to server.',
      timer: 1500,
      showConfirmButton: false,
      toast: true,
      position: 'top-end',
    });
  };

  // ============================================================
  // REMOVE FILE
  // ============================================================
  const removeFile = async (fileId) => {
    const fileToRemove = files.find(f => f.id === fileId);

    if (fileToRemove?.isSaved && trackingId && trackingId !== 'new') {
      const result = await Swal.fire({
        icon: 'warning',
        title: 'Delete File?',
        text: 'This file will be permanently deleted from the server.',
        showCancelButton: true,
        confirmButtonColor: '#dc2626',
        cancelButtonColor: '#666',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel',
      });

      if (!result.isConfirmed) return;

      try {
        const url = getDeleteUrl(fileId);
        console.log('🗑️ Deleting:', url);

        const response = await fetch(url, {
          method: 'DELETE',
          headers: buildHeaders(false),
        });

        if (!response.ok && response.status !== 204) {
          throw new Error(`Failed to delete: ${response.status}`);
        }

        setFiles(prev => prev.filter(f => f.id !== fileId));

        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          timer: 1500,
          showConfirmButton: false,
          toast: true,
          position: 'top-end',
        });
      } catch (err) {
        console.error('Delete error:', err);
        Swal.fire({
          icon: 'error',
          title: 'Delete Failed',
          text: err.message,
          confirmButtonColor: '#dc2626',
        });
      }
    } else {
      setFiles(prev => prev.filter(f => f.id !== fileId));
    }
  };

  const viewFile = (file) => {
    if (file.isSaved && file.url) {
      if ((file.type && file.type.includes('image')) || file.category === 'image') {
        setSelectedFile(file);
        setShowPreview(true);
      } else {
        window.open(file.url, '_blank');
      }
    } else if (file.file) {
      if (file.file.type.includes('image')) {
        setSelectedFile(file);
        setShowPreview(true);
      } else {
        window.open(URL.createObjectURL(file.file), '_blank');
      }
    }
  };

  const downloadFile = (file) => {
    if (file.isSaved && file.url) {
      const a = document.createElement('a');
      a.href = file.url;
      a.download = file.name;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else if (file.file) {
      const url = URL.createObjectURL(file.file);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const replaceFile = (fileId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.onchange = (e) => {
      const newFile = e.target.files[0];
      if (newFile) {
        const category = detectCategoryFromName(newFile.name);
        setFiles(files.map(f =>
          f.id === fileId ? {
            ...f,
            name: newFile.name,
            type: newFile.type,
            category,
            size: newFile.size,
            updated: new Date().toLocaleDateString(),
            file: newFile,
            preview: newFile.type.includes('image') ? URL.createObjectURL(newFile) : null,
            isSaved: false,
          } : f
        ));
      }
    };
    input.click();
  };

  const getFileIcon = (fileType, fileName) => {
    if (fileType?.includes('image')) return '🖼️';
    if (fileType?.includes('zip') || fileName?.includes('.zip')) return '📦';
    if (fileName?.includes('.pdf')) return '📑';
    if (fileName?.includes('.doc') || fileName?.includes('.docx')) return '📝';
    if (fileName?.includes('.xls') || fileName?.includes('.xlsx')) return '📊';
    if (fileName?.toLowerCase().includes('scan')) return '🔍';
    return '📄';
  };

  const closePreview = () => {
    setShowPreview(false);
    setSelectedFile(null);
  };

  // ============================================================
  // UPLOAD FILES (auto/manual)
  // ============================================================
  const handleSaveFiles = async (providedTrackingId = null) => {
    const effectiveTrackingId = providedTrackingId || trackingId;

    if (!effectiveTrackingId || effectiveTrackingId === 'new') {
      Swal.fire({
        icon: 'warning',
        title: 'Save Main Form First',
        text: 'Please save the record first. Files will be uploaded automatically.',
        confirmButtonColor: '#004aad',
      });
      return { success: false, reason: 'no_tracking_id' };
    }

    if (mode === 'lot_tracking' && !supplierId) {
      Swal.fire({
        icon: 'error',
        title: 'Missing Supplier ID',
        text: 'Lot Tracking mode requires supplierId prop.',
        confirmButtonColor: '#dc2626',
      });
      return { success: false, reason: 'no_supplier_id' };
    }

    const unsavedFiles = files.filter(f => !f.isSaved && f.file);

    if (unsavedFiles.length === 0) {
      return { success: true, uploaded: 0 };
    }

    try {
      setSaving(true);
      const uploadUrl = getUploadUrl();

      let uploadedCount = 0;
      const uploadedServerFiles = [];

      for (const fileObj of unsavedFiles) {
        const formData = new FormData();
        formData.append('image', fileObj.file);
        formData.append('image_type', fileObj.category === 'image' ? 'image' : fileObj.category);

        const headers = {};
        const token = getAuthToken();
        if (token && token !== 'null' && token !== 'undefined') {
          headers['Authorization'] = `Bearer ${token}`;
        }

        console.log(`📤 [${mode}] Uploading:`, fileObj.name, '→', uploadUrl);

        const response = await fetch(uploadUrl, {
          method: 'POST',
          headers,
          body: formData,
        });

        if (!response.ok) {
          let errMsg = `HTTP ${response.status}`;
          try {
            const t = await response.text();
            errMsg += ` - ${t}`;
          } catch (_) {}
          throw new Error(`Upload failed for ${fileObj.name}: ${errMsg}`);
        }

        const data = await response.json();
        console.log('✅ Uploaded:', data);
        uploadedCount++;
        uploadedServerFiles.push({ local: fileObj, server: data });
      }

      setFiles(prev => prev.map(f => {
        const match = uploadedServerFiles.find(u => u.local.id === f.id);
        if (match) {
          const serverUrl = extractFileUrl(match.server) || f.preview;
          return {
            ...f,
            isSaved: true,
            url: serverUrl,
            id: match.server.id || f.id,
          };
        }
        return f;
      }));

      return { success: true, uploaded: uploadedCount };
    } catch (err) {
      console.error('❌ Save files error:', err);
      return { success: false, error: err.message };
    } finally {
      setSaving(false);
    }
  };

  const handleSaveFilesManual = async () => {
    if (!trackingId || trackingId === 'new') {
      Swal.fire({
        icon: 'warning',
        title: 'Save Main Form First',
        text: 'Please save the record first.',
        confirmButtonColor: '#004aad',
      });
      return;
    }

    const unsavedCount = files.filter(f => !f.isSaved && f.file).length;
    if (unsavedCount === 0) {
      Swal.fire({
        icon: 'info',
        title: 'No New Files',
        text: 'All files are already saved.',
        confirmButtonColor: '#004aad',
        timer: 1500,
        showConfirmButton: false,
      });
      return;
    }

    const result = await handleSaveFiles();

    if (result.success) {
      Swal.fire({
        icon: 'success',
        title: 'Files Saved!',
        text: `${result.uploaded} file(s) uploaded successfully.`,
        confirmButtonColor: '#004aad',
        timer: 2000,
        timerProgressBar: true,
      });
    } else if (result.error) {
      Swal.fire({
        icon: 'error',
        title: 'Upload Failed',
        text: result.error,
        confirmButtonColor: '#dc2626',
      });
    }
  };

  const unsavedCount = files.filter(f => !f.isSaved && f.file).length;
  const canSaveToServer = trackingId && trackingId !== 'new' &&
                          (mode !== 'lot_tracking' || supplierId);

  const imageFiles = files.filter(f => f.category === 'image' || f.type?.includes('image'));
  const scanFiles = files.filter(f => f.category === 'scan' || f.name?.toLowerCase().includes('scan'));
  const zipFiles = files.filter(f => f.category === 'zip' || f.type?.includes('zip') || f.name?.includes('.zip'));
  const otherFiles = files.filter(f =>
    f.category !== 'image' && !f.type?.includes('image') &&
    f.category !== 'scan' && !f.name?.toLowerCase().includes('scan') &&
    f.category !== 'zip' && !f.type?.includes('zip') && !f.name?.includes('.zip')
  );

  const renderFileCard = (file) => (
    <div key={file.id} className={`file-card-vertical ${!file.isSaved ? 'unsaved' : ''}`}>
      <div className="file-thumbnail-vertical" onClick={() => viewFile(file)}>
        {(file.preview || file.url) && (file.type?.includes('image') || file.category === 'image') ? (
          <img src={file.preview || file.url} alt={file.name} className="file-thumbnail-img-vertical" />
        ) : (
          <span className="file-icon-large-vertical">{getFileIcon(file.type, file.name)}</span>
        )}
      </div>
      <div className="file-name-vertical" title={file.name}>{file.name}</div>
      {!file.isSaved && <span className="unsaved-badge">● Unsaved</span>}
      {file.isSaved && file.isMirror && (
        <span className="unsaved-badge" style={{ background: '#dbeafe', color: '#1e40af' }}>🔗 Synced</span>
      )}
      <div className="file-icons-vertical">
        <FaRegEye className="file-action-icon-vertical" onClick={() => viewFile(file)} title="View" />
        <MdOutlineDownloadForOffline className="file-action-icon-vertical" onClick={() => downloadFile(file)} title="Download" />
        <BsArrowRepeat className="file-action-icon-vertical" onClick={() => replaceFile(file.id)} title="Replace" />
        <RiDeleteBin6Fill className="file-action-icon-vertical delete-icon-vertical" onClick={() => removeFile(file.id)} title="Delete" />
      </div>
      <div className="file-meta-vertical">
        <div className="meta-line-vertical">Date: {file.date}</div>
        <div className="meta-line-vertical">Updated: {file.updated}</div>
        <div className="meta-line-vertical">From: {file.from}</div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="details-content">
        <div className="details-header"><h4>File Details</h4></div>
        <div className="no-files">Loading files...</div>
      </div>
    );
  }

  return (
    <div className="details-content">
      <div className="details-header">
        <h4>File Details</h4>
        <div className="details-header-actions">
          <label className="add-file-btn">
            <IoAddCircleSharp size={20} />
            Add File
            <input type="file" multiple onChange={handleFileUpload} style={{ display: 'none' }} />
          </label>

          {canSaveToServer && unsavedCount > 0 && (
            <button type="button" className="save-files-btn" onClick={handleSaveFilesManual} disabled={saving}>
              <FiSave size={16} />
              {saving ? 'Saving...' : `Save Files (${unsavedCount})`}
            </button>
          )}
        </div>
      </div>

      {!canSaveToServer && unsavedCount > 0 && (
        <div className="unsaved-files-banner">
          💡 Save the main form first — files will upload automatically.
        </div>
      )}

      {canSaveToServer && unsavedCount > 0 && (
        <div className="unsaved-files-banner">
          ⚠️ You have {unsavedCount} unsaved file(s). Click <strong>"Save Files"</strong> to upload.
        </div>
      )}

      <div className="file-categories">
        <div className="file-category">
          <h5>Image Files</h5>
          <div className="file-list-vertical">
            {imageFiles.map(renderFileCard)}
            {imageFiles.length === 0 && (<div className="no-files">No image files uploaded</div>)}
          </div>
        </div>

        <div className="file-category">
          <h5>Intraoral Scan Files</h5>
          <div className="file-list-vertical">
            {scanFiles.map(renderFileCard)}
            {scanFiles.length === 0 && (<div className="no-files">No intraoral scan files uploaded</div>)}
          </div>
        </div>

        <div className="file-category">
          <h5>Zip Files</h5>
          <div className="file-list-vertical">
            {zipFiles.map(renderFileCard)}
            {zipFiles.length === 0 && (<div className="no-files">No zip files uploaded</div>)}
          </div>
        </div>

        <div className="file-category">
          <h5>Other Files</h5>
          <div className="file-list-vertical">
            {otherFiles.map(renderFileCard)}
            {otherFiles.length === 0 && (<div className="no-files">No other files uploaded</div>)}
          </div>
        </div>
      </div>

      {showPreview && selectedFile && (
        <div className="preview-modal" onClick={closePreview}>
          <div className="preview-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="preview-modal-header">
              <h3>{selectedFile.name}</h3>
              <IoCloseCircleSharp className="close-modal" onClick={closePreview} size={24} />
            </div>
            <div className="preview-modal-body">
              {(selectedFile.preview || selectedFile.url) && (selectedFile.type?.includes('image') || selectedFile.category === 'image') ? (
                <img src={selectedFile.preview || selectedFile.url} alt={selectedFile.name} className="preview-image" />
              ) : (
                <div className="preview-file-info">
                  <span className="preview-file-icon">{getFileIcon(selectedFile.type, selectedFile.name)}</span>
                  <p>File: {selectedFile.name}</p>
                  <p>Size: {((selectedFile.size || 0) / 1024).toFixed(2)} KB</p>
                  <p>Type: {selectedFile.type || 'Unknown'}</p>
                  <p>Uploaded: {selectedFile.date}</p>
                  <p>From: {selectedFile.from}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

Details.displayName = 'Details';
export default Details;